import React, { useEffect } from 'react';
import { Tabs, Tab as MuiTab, TabProps, TabsProps, SxProps, Theme } from '@mui/material';
import { a11yProps, CustomTabPanel, TabPanelProps } from 'components/utils';

type CommonTabProps = {
  label?: React.ReactNode;
  value: number;
  isHidden?: boolean;
  renderControl?: (props: TabProps) => React.ReactNode;
};

type Props = {
  initialTabValue?: number;
  tabsProps?: TabsProps;
  tabs: CommonTabProps[];
  extraTabContent?: React.ReactNode;
  tabPanels?: {
    value: number;
    key?: string;
    renderContent: () => React.ReactNode;
  }[];
  onTabChange?: (tabValue: number) => void;
  variant: 'fullWidth' | 'scrollable' | 'standard';
};

const titleStyle: SxProps<Theme> = {
  margin: '4px 2px',
  padding: '8px 16px',
  minHeight: '36px',
  borderRadius: '4px',
  backgroundColor: 'transparent',
  color: 'text.secondary',
  '&.Mui-selected': {
    backgroundColor: 'background.paper',
    color: 'text.primary',
  },
  '&:hover': {
    backgroundColor: 'action.hover',
    color: 'text.primary',
  },
};

const tabPanelStyle = {
  padding: '16px 0',
};

export const Tab = ({ label, value, ...props }: TabProps) => {
  return (
    <MuiTab
      label={label}
      value={value}
      sx={{
        margin: '4px 2px',
        padding: '8px 16px',
        minHeight: '36px',
        borderRadius: '4px',
        backgroundColor: 'transparent',
        color: 'text.secondary',
        '&.Mui-selected': {
          backgroundColor: 'background.paper',
          color: 'text.primary',
        },
        '&:hover': {
          backgroundColor: 'action.hover',
          color: 'text.primary',
        },
      }}
      {...props}
    />
  );
};

const CommonTabs = ({
  tabs,
  tabPanels = [],
  onTabChange,
  initialTabValue,
  tabsProps,
  extraTabContent,
  variant,
}: Props) => {
  const [tabValue, setTabValue] = React.useState(initialTabValue || 0);

  useEffect(() => {
    if (initialTabValue) {
      setTabValue(initialTabValue);
    }
  }, [initialTabValue]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    if (onTabChange) {
      onTabChange(newValue);
    }

    setTabValue(newValue);
  };

  const renderTab = (tab: CommonTabProps) => {
    if (tab.isHidden) {
      return null;
    }

    if (tab.renderControl) {
      return tab.renderControl({ label: tab.label, value: tab.value });
    }

    return (
      <MuiTab
        key={tab.value}
        value={tab.value}
        label={tab.label}
        {...a11yProps(tab.value)}
        sx={titleStyle}
        onClick={() => {
          setTabValue(tab.value);
        }}
      />
    );
  };

  return (
    <>
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        sx={{
          position: 'relative',
          backgroundColor: 'divider',
          borderRadius: '8px',
          minHeight: '36px',
          padding: '0 2px',
          '& .MuiTabs-indicator': {
            backgroundColor: 'transparent',
          },
        }}
        variant={variant}
        {...tabsProps}
      >
        {tabs.map(tab => renderTab(tab))}

        {extraTabContent}
      </Tabs>

      {tabPanels.map(tabPanel => (
        <CustomTabPanel
          key={tabPanel.key || tabPanel.value}
          value={tabValue}
          index={tabPanel.value}
          style={tabPanelStyle}
        >
          {tabPanel.renderContent()}
        </CustomTabPanel>
      ))}
    </>
  );
};

export const TabPanel = ({ children, ...props }: TabPanelProps) => {
  if (props.isHidden) {
    return null;
  }

  return (
    <CustomTabPanel {...props} style={tabPanelStyle}>
      {children}
    </CustomTabPanel>
  );
};

export default CommonTabs;
